import { MODULE_SCOPES, PermissionAction, PermissionModule } from "@ibada/shared-types/authorization"
import { RoleScope } from "@ibada/shared-types/workspace"

import { ModuleAccessLevel } from "@/features/users/model/team-panel"

/** The permissions of one role, as the role catalog (`GET /roles`, what "Voir l'accès" reads) returns them. */
export type RolePermissionsSource = {
  key: string
  permissions: readonly string[]
}

export type ModuleAccessRow = {
  module: PermissionModule
  level: ModuleAccessLevel
}

/** The order modules are listed in: the day-to-day ones first, governance last. */
const MODULE_ORDER: readonly PermissionModule[] = [
  PermissionModule.Members,
  PermissionModule.Finance,
  PermissionModule.Budgets,
  PermissionModule.Activities,
  PermissionModule.Assets,
  PermissionModule.Groups,
  PermissionModule.Ministries,
  PermissionModule.Departments,
  PermissionModule.Forms,
  PermissionModule.Reports,
  PermissionModule.Users,
  PermissionModule.WorkspaceSettings,
  PermissionModule.Settings,
  PermissionModule.Branches,
  PermissionModule.Roles,
  PermissionModule.Subscription,
]

/** The modules a role of this scope can grant (MODULE_SCOPES is the authority). */
export function modulesForScope(scope: RoleScope, options: { includeWorkspaceSettings?: boolean } = {}): PermissionModule[] {
  return MODULE_ORDER.filter((module) => {
    if (module === PermissionModule.WorkspaceSettings && !options.includeWorkspaceSettings) return false
    return MODULE_SCOPES[module].includes(scope)
  })
}

/**
 * What someone can do in each module, read from the roles they hold here: Modifier as soon as one role grants
 * anything beyond reading (edit, approve, manage), Voir for read only, Aucun otherwise.
 *
 * A summary for reading, not an authorization decision: the backend evaluates the same roles on every request.
 */
export function resolveModuleAccess(
  heldRoleKeys: readonly string[],
  catalog: readonly RolePermissionsSource[],
  modules: readonly PermissionModule[],
): ModuleAccessRow[] {
  const held = new Set(heldRoleKeys)
  const granted = new Set<string>()
  for (const role of catalog) {
    if (!held.has(role.key)) continue
    for (const permission of role.permissions) granted.add(permission)
  }

  return modules.map((module) => {
    const canChange = [PermissionAction.Edit, PermissionAction.Approve, PermissionAction.Manage].some((action) =>
      granted.has(`${module}.${action}`),
    )
    if (canChange) return { module, level: ModuleAccessLevel.Edit }
    if (granted.has(`${module}.${PermissionAction.View}`)) return { module, level: ModuleAccessLevel.View }
    return { module, level: ModuleAccessLevel.None }
  })
}

const LEVEL_RANK: Record<ModuleAccessLevel, number> = {
  [ModuleAccessLevel.None]: 0,
  [ModuleAccessLevel.View]: 1,
  [ModuleAccessLevel.Edit]: 2,
}

export type CombinedModuleAccessRow = ModuleAccessRow & {
  /** The branches do not all reach this level: shown with "selon la branche". */
  varies: boolean
}

/**
 * Several branches with their own roles: each module at the highest level any branch reaches, flagged when the
 * branches disagree. One branch (or the same roles everywhere) never varies.
 */
export function resolveModuleAccessAcrossBranches(
  roleKeysPerBranch: readonly (readonly string[])[],
  catalog: readonly RolePermissionsSource[],
  modules: readonly PermissionModule[],
): CombinedModuleAccessRow[] {
  const perBranch = (roleKeysPerBranch.length > 0 ? roleKeysPerBranch : [[]]).map((keys) =>
    resolveModuleAccess(keys, catalog, modules),
  )
  return modules.map((module, index) => {
    const levels = perBranch.map((rows) => rows[index].level)
    const level = levels.reduce((best, next) => (LEVEL_RANK[next] > LEVEL_RANK[best] ? next : best), ModuleAccessLevel.None)
    return { module, level, varies: levels.some((entry) => entry !== levels[0]) }
  })
}
